import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { v4 as uuidv4 } from "uuid";

import { clinicalDiagramSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useUpdateClinicalDiagramDetailsMutation, useGetClinicalDiagramDetailQuery, useFetchS3ObjectMutation } from "../../services/api";
import style from "./clinicalDiagram.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { ImageInput } from "../../components/UserInput";
import defaultImage from "../../assets/images/default-image.png";
import { getExtension } from "../../utils/common";
import type { ApiError, ClinicalDiagramFormValues } from "./clinicalDiagramTypes";
import { getApiErrorMessage, uploadClinicalDiagramFile } from "./clinicalDiagramUtils";

type FormState = ClinicalDiagramFormValues;

/**
 * Renders the Edit Clinical Diagram form.
 *
 * The component loads existing diagram details, hydrates form state, uploads
 * updated assets to S3 when provided, and submits an update mutation.
 *
 * Returns:
 *   JSX.Element: The Edit Clinical Diagram page content.
 */
const EditClinicalDiagram = () => {
  const [updateClinicalDiagram] = useUpdateClinicalDiagramDetailsMutation();
  const [fetchS3Object] = useFetchS3ObjectMutation();
  const { id } = useParams();
  const navigate = useNavigate();

  // Get Clinical Diagram Details mutation hook
  const {
    data: clinicalDiagramDetails,
    isLoading,
    isFetching,
  } = useGetClinicalDiagramDetailQuery(id ?? "");

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [isSavingClinicalDiagram, setIsSavingClinicalDiagram] = useState(false);
  const [diagramError, setDiagramError] = useState(false)
  const [thumbnail, setThumbnail] = useState<File>();
  const [diagram, setDiagram] = useState<File>();

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(clinicalDiagramSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormState>({
    resolver: yupResolver(clinicalDiagramSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!isEmpty(clinicalDiagramDetails)) {
      reset({
        name: clinicalDiagramDetails?.name ?? "",
      });
    }
  }, [clinicalDiagramDetails, reset]);

  const onSubmit = async (values: FormState) => {
    // Guard against missing route param to avoid invalid update calls.
    if (!id) {
      setMessage({
        type: "error",
        text: "Missing clinical diagram identifier. Please refresh and try again.",
      });
      return;
    }
    let didStartSaving = false;
    try {
      const { name } = values;
      // Require at least existing or newly provided assets before update.
      if (
        typeof clinicalDiagramDetails?.thumbnail_pre_signed_url !== "string" &&
        thumbnail === undefined
      ) {
        setThumbnailError(true);
        return;
      }
      if (typeof clinicalDiagramDetails?.diagram_pre_signed_url !== "string" &&
        diagram === undefined) {
        setDiagramError(true);
        return;
      }
      setIsSavingClinicalDiagram(true);
      didStartSaving = true;

      let payload: EditClinicalDiagramPayload = {
        id: parseInt(id, 10),
        name: name,
      };

      if (thumbnail) {
        // Reuse existing S3 key when the name is unchanged; otherwise create a new unique key.
        const fileExtension = getExtension(thumbnail?.name ?? "");
        const keyName = (clinicalDiagramDetails?.thumbnail_path && clinicalDiagramDetails?.name === name)
          ? clinicalDiagramDetails?.thumbnail_path
          : `static/clinical_diagram/clinical_diagram_${name}/thumbnail_${uuidv4()}.${fileExtension}`;
        const uploadedKey = await uploadClinicalDiagramFile({
          file: thumbnail,
          fetchS3Object,
          keyName,
        });
        if (uploadedKey) {
          payload = { ...payload, thumbnail_path: uploadedKey };
        }
      }
      if (diagram) {
        // Diagram asset uses the same reuse/new key strategy as thumbnail.
        const fileExtension = getExtension(diagram?.name ?? "");
        const keyName = (clinicalDiagramDetails?.diagram_path && clinicalDiagramDetails?.name === name)
          ? clinicalDiagramDetails?.diagram_path
          : `static/clinical_diagram/clinical_diagram_${name}/diagram_${uuidv4()}.${fileExtension}`;
        const uploadedKey = await uploadClinicalDiagramFile({
          file: diagram,
          fetchS3Object,
          keyName,
        });
        if (uploadedKey) {
          payload = { ...payload, diagram_path: uploadedKey };
        }
      }

      await updateClinicalDiagram(payload).unwrap();
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      setMessage({
        type: "error",
        text: getApiErrorMessage(error as ApiError, "Please enter valid details"),
      });
    } finally {
      if (didStartSaving) {
        setIsSavingClinicalDiagram(false);
      }
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading2}
            sx={{ marginTop: "10px" }}
          >
            Clinical Diagram Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="e.g Right Arm"
                label="Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
          </Box>
          <Box className={style.row} sx={{ flexDirection: "column", marginTop: "20px" }}>
            <Box component="label" htmlFor="thumbnail-input" sx={{ mb: -2, display: "flex", alignItems: "center" }} className={style.inputLabel}>
              Upload Thumbnail
              <Box component="span" sx={{ color: "red", ml: 0.7 }}>*</Box>
            </Box>
            <Box sx={{ width: "100%", }}>
              <ImageInput
                label="Thumbnail"
                required
                register={register}
                name="thumbnail"
                error={thumbnailError}
                onImageSelected={(images) => {
                  setThumbnailError(false);
                  setThumbnail(images[0]);
                }}
                height={"250px"}
                width={"100%"}
                borderRadius="6"
                defaultValue={clinicalDiagramDetails?.thumbnail_pre_signed_url ?? defaultImage}
              />
            </Box>
          </Box>
          <Box className={style.row} sx={{ flexDirection: "column", marginTop: "5px" }}>
            <Box component="label" htmlFor="thumbnail-input" sx={{ mb: -2, display: "flex", alignItems: "center" }} className={style.inputLabel}>
              Upload Diagram
              <Box component="span" sx={{ color: "red", ml: 0.7 }}>*</Box>
            </Box>
            <Box sx={{ width: "100%", }}>
              <ImageInput
                label="Diagram"
                required
                register={register}
                name="diagram"
                error={diagramError}
                onImageSelected={(images) => {
                  setDiagramError(false);
                  setDiagram(images[0]);
                }}
                height={"250px"}
                width={"100%"}
                borderRadius="6"
                defaultValue={clinicalDiagramDetails?.diagram_pre_signed_url ?? defaultImage}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigate(-1)}
            label={"Cancel"}
          />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingClinicalDiagram}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditClinicalDiagram;
